/**
 * ComparisonTable component for marketing pages
 * Reusable comparison table for Gmail vs Email Ferret features
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

export interface ComparisonRow {
  /**
   * Feature name to compare
   * @example "Detects AI cold outreach"
   */
  feature: string;
  /**
   * Email Ferret's value (boolean or string)
   * @example true, false, "Yes", "No", "Advanced"
   */
  emailFerret: boolean | string;
  /**
   * Gmail's value (boolean or string)
   * @example true, false, "Yes", "No", "Basic"
   */
  gmail: boolean | string;
  /**
   * Whether to highlight this row (Email Ferret advantage)
   * @default false
   */
  highlight?: boolean;
}

export interface ComparisonTableProps {
  /**
   * Array of comparison rows
   */
  rows: ComparisonRow[];
  /**
   * Optional table caption
   */
  caption?: string;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Formats a value for display in the table
 * - boolean true → Check icon
 * - boolean false → X icon
 * - string → Display as-is
 */
function formatValue(value: boolean | string): React.ReactNode {
  if (typeof value === 'boolean') {
    return value ? (
      <div className="flex justify-center">
        <Check className="h-6 w-6 text-green-500" strokeWidth={2.5} aria-label="Yes" />
      </div>
    ) : (
      <div className="flex justify-center">
        <X className="h-6 w-6 text-red-400/80" strokeWidth={2.5} aria-label="No" />
      </div>
    );
  }
  return <span className="font-medium text-foreground/90">{value}</span>;
}

/**
 * ComparisonTable component
 *
 * Displays a comparison table between Email Ferret and Gmail spam filters.
 * Supports boolean values (displayed as checkmarks/X) and string values.
 * Can highlight rows where Email Ferret has an advantage.
 *
 * @param rows - Array of comparison rows
 * @param caption - Optional table caption
 * @param className - Optional additional CSS classes
 *
 * @example
 * ```tsx
 * <ComparisonTable
 *   rows={[
 *     {
 *       feature: "Detects AI cold outreach",
 *       emailFerret: true,
 *       gmail: false,
 *       highlight: true
 *     },
 *     {
 *       feature: "Shows why flagged",
 *       emailFerret: true,
 *       gmail: false,
 *       highlight: true
 *     }
 *   ]}
 *   caption="Feature comparison: Email Ferret vs Gmail spam filters"
 * />
 * ```
 */
export function ComparisonTable({
  rows,
  caption,
  className = '',
}: ComparisonTableProps): React.ReactElement {
  return (
    <div className={cn(className)}>
      {/* Mobile: stacked card layout */}
      <div className="sm:hidden space-y-3" role="list" aria-label={caption || 'Feature comparison'}>
        {rows.map((row) => (
          <div
            key={row.feature}
            role="listitem"
            className={cn(
              'rounded-lg border border-border/40 p-4',
              row.highlight && 'bg-blue-500/8'
            )}
          >
            <p className="font-semibold text-foreground text-sm mb-3">{row.feature}</p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Email Ferret</span>
                {formatValue(row.emailFerret)}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Gmail</span>
                {formatValue(row.gmail)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table layout */}
      <div className="hidden sm:block overflow-x-auto">
        <table
          className="w-full border-collapse"
          aria-label={caption || 'Feature comparison table'}
        >
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b-2 border-border/60">
              <th
                scope="col"
                className="text-left py-4 px-6 font-semibold text-foreground text-base"
                aria-label="Feature"
              >
                Feature
              </th>
              <th
                scope="col"
                className="text-center py-4 px-6 font-semibold text-foreground text-base"
                aria-label="Email Ferret"
              >
                Email Ferret
              </th>
              <th
                scope="col"
                className="text-center py-4 px-6 font-semibold text-muted-foreground text-base"
                aria-label="Gmail Spam Filter"
              >
                Gmail Spam Filter
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.feature}
                className={cn(
                  'border-b border-border/40 transition-colors duration-200',
                  row.highlight && 'bg-blue-500/8 hover:bg-blue-500/15'
                )}
              >
                <td className="py-4 px-6 font-semibold text-foreground">{row.feature}</td>
                <td className="py-4 px-6 text-center">{formatValue(row.emailFerret)}</td>
                <td className="py-4 px-6 text-center">{formatValue(row.gmail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
